import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ChangeCircleRoundedIcon from "@mui/icons-material/ChangeCircleRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Person2RoundedIcon from "@mui/icons-material/Person2Rounded";
import {
  alpha,
  Box,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FormattedPrice,
  PriceDiff,
} from "components/common/planHistory/PriceDiff";
import { FormattedMass } from "components/common/planHistory/FormattedMass";
import { SteelGradeNames } from "components/common/planHistory/SteelGradeNames";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTenantTranslation } from "hooks/formatters";
import type {
  MixDifference,
  MixDifferences,
  PlanDifferences,
} from "store/api/generatedApi";

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

interface Props {
  isLoading: boolean;
  ancestors: PlanDifferences[];
}

export const PlanAncestors = ({ isLoading, ancestors }: Props) => {
  const { t } = useTenantTranslation();

  if (isLoading) {
    return <PlanAncestorsSkeleton />;
  }

  const planAncestors = ancestors.slice().reverse();

  if (!planAncestors.length) {
    return (
      <Typography sx={{ color: "text.secondary", px: 2, py: 1 }}>
        {t("noDifferencesFound")}
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {planAncestors.map((item, index) => (
        <PlanAncestorItem
          key={`plan-${item.target_plan_id}`}
          item={item}
          index={index}
          totalAncestors={planAncestors.length}
          t={t}
        />
      ))}
    </Stack>
  );
};

const PlanAncestorsSkeleton = () => {
  return (
    <Stack spacing={2} p={1} sx={{ width: "90%" }}>
      <Skeleton variant="text" width="60%" height={30} />
      <Skeleton variant="text" width="30%" height={20} sx={{ mt: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={60} sx={{ mt: 1 }} />
    </Stack>
  );
};

interface PlanAncestorItemProps {
  item: PlanDifferences;
  index: number;
  totalAncestors: number;
  t: (key: string) => string;
}

const PlanAncestorItem = ({
  item,
  index,
  totalAncestors,
  t,
}: PlanAncestorItemProps) => {
  return (
    <Box
      sx={{
        borderRadius: "4px",
        "&:hover": {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
        },
      }}
    >
      <AncestorHeader
        item={item}
        index={index}
        totalAncestors={totalAncestors}
        t={t}
      />
      <MixDifferencesTable item={item} t={t} />
    </Box>
  );
};

interface AncestorHeaderProps {
  item: PlanDifferences;
  index: number;
  totalAncestors: number;
  t: (key: string) => string;
}

const AncestorHeader = ({
  item,
  index,
  totalAncestors,
  t,
}: AncestorHeaderProps) => {
  const deployment = item.target_deployments[0];
  const isEditedDeployment = ["edited_standard", "edited_backup"].includes(
    deployment?.deployment_type ?? ""
  );
  const displayName =
    deployment && !isEditedDeployment
      ? deployment.deployed_by_name
      : item.target_created_by_name;
  const timestamp =
    deployment && !isEditedDeployment
      ? new Date(deployment.deployed_at)
      : new Date(item.target_created_at);

  const dayjsTimestamp = dayjs(timestamp);
  const formattedDate = dayjsTimestamp.format("ll"); // localised abbreviated date
  const formattedTime = dayjsTimestamp.format("LT"); // localised time
  const relativeTime = dayjsTimestamp.fromNow();
  return (
    <Box sx={{ pt: 1.5, px: 2, borderTop: "1px solid #e0e0e0" }}>
      <Grid container spacing={1}>
        <Grid size={{ xs: 6 }}>
          <Tooltip
            title={`${
              deployment
                ? isEditedDeployment
                  ? t("editedBy")
                  : t("deployedBy")
                : t("createdBy")
            } ${displayName}`}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ color: "text.secondary" }}
            >
              <Person2RoundedIcon sx={{ fontSize: 22 }} />
              <Typography variant="h6">{displayName}</Typography>
            </Stack>
          </Tooltip>
          <Tooltip
            title={`${
              deployment
                ? isEditedDeployment
                  ? t("editedAt")
                  : t("deployedAt")
                : t("createdAt")
            }: ${formattedDate} ${formattedTime}`}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                color: "text.secondary",
                mt: 0.5,
              }}
            >
              <AccessTimeRoundedIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ fontStyle: "italic" }}>
                {relativeTime}
              </Typography>
            </Stack>
          </Tooltip>
        </Grid>

        <Grid size={{ xs: 6 }}>
          <Stack spacing={0.5} alignItems="flex-end">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip
                title={`${t("plan")}: ${item.source_plan_id} → ${
                  item.target_plan_id
                }`}
              >
                <Chip
                  icon={<ChangeCircleRoundedIcon />}
                  label={`${t("change")} ${totalAncestors - index}`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: deployment
                      ? (theme) => alpha(theme.palette.success.main, 0.1)
                      : (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: deployment ? "success.main" : "primary.main",
                    "& .MuiChip-icon": {
                      color: "inherit",
                    },
                  }}
                />
              </Tooltip>
              {deployment ? (
                <Tooltip
                  title={
                    <>
                      {`${t("createdAt")}: ${dayjs(timestamp).format("ll")}`}
                      <br />
                      {`${t("deployedAt")}: ${dayjs(timestamp).format("ll")}`}
                    </>
                  }
                >
                  <CheckCircleRoundedIcon
                    sx={{ color: "success.main", fontSize: 20 }}
                  />
                </Tooltip>
              ) : null}
            </Stack>

            <PriceDiff
              priceDiff={
                item.summary.target_display_cost_per_tonne -
                item.summary.source_display_cost_per_tonne
              }
              tooltipTitle={
                <Stack direction="row" spacing={1}>
                  <FormattedPrice
                    value={item.summary.source_display_cost_per_tonne}
                    variant="unformatted"
                  />
                  <span>→</span>
                  <FormattedPrice
                    value={item.summary.target_display_cost_per_tonne}
                    variant="unformatted"
                  />
                </Stack>
              }
            />
          </Stack>
        </Grid>
      </Grid>
      {Boolean(item.target_comment) && (
        <Box sx={{ pt: 1, px: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              backgroundColor: (theme) => alpha(theme.palette.info.main, 0.05),
              border: (theme) =>
                `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              borderRadius: 1,
              padding: 1,
              maxWidth: "100%",
              wordBreak: "break-word",
            }}
          >
            {item.target_comment}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

interface MixDifferencesTableProps {
  item: PlanDifferences;
  t: (key: string) => string;
}

const MixDifferencesTable = ({ item, t }: MixDifferencesTableProps) => {
  const mixDifferences = item.differences;

  return (
    <Table
      size="small"
      sx={{ width: "100%", tableLayout: "fixed", "& td, & th": { py: 0.5 } }}
    >
      <TableBody>
        {mixDifferences.map((mixDiff: MixDifferences) => (
          <MixDifferenceGroup
            key={`mix-${mixDiff.target_mix_id}`}
            targetMixId={mixDiff.target_mix_id.toString()}
            steelGradeNames={mixDiff.target_steel_grade_names}
            materialDifferences={mixDiff.differences.filter(
              (diff) => Math.abs(diff.value_difference) >= 0.1
            )}
            t={t}
          />
        ))}
      </TableBody>
    </Table>
  );
};

interface MixDifferenceGroupProps {
  targetMixId: string;
  steelGradeNames: string[];
  materialDifferences: MixDifference[];
  t: (key: string) => string;
}

const MixDifferenceGroup = ({
  targetMixId,
  steelGradeNames,
  materialDifferences,
  t,
}: MixDifferenceGroupProps) => {
  return (
    <TableRow>
      <TableCell sx={{ border: "none", overflow: "hidden" }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box
            sx={{
              position: "relative",
              width: 20,
              alignSelf: "stretch",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                position: "relative",
                top: 8,
                left: 12,
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                left: 15,
                top: 8,
                bottom: 0,
                width: 2,
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.3),
              }}
            />
          </Box>
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0, pt: 0.1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption">
                {t("mix")} {targetMixId}
              </Typography>
              <SteelGradeNames steelGradeNames={steelGradeNames} />
            </Stack>
            {materialDifferences.map((diff) => (
              <MaterialDifferenceRow
                key={`${diff.material_name}-${diff.basket_number}`}
                diff={diff}
                t={t}
              />
            ))}
          </Stack>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

interface MaterialDifferenceRowProps {
  diff: MixDifference;
  t: (key: string) => string;
}

const MaterialDifferenceRow = ({ diff, t }: MaterialDifferenceRowProps) => {
  return (
    <Stack
      key={`${diff.material_name}-${diff.basket_number}`}
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        pl: 0,
        py: 0.1,
        marginTop: "0px !important",
        "&:hover": {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
        },
      }}
    >
      <Box sx={{ width: 55, flexShrink: 0, textAlign: "right" }}>
        <FormattedMass
          value={diff.value_difference}
          unit={diff.unit}
          showSign
        />
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.75rem",
          flex: 1,
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {diff.material_name}
      </Typography>
      {diff.addition_location === "basket" && (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.75rem",
            color: "text.secondary",
            width: 55,
            flexShrink: 0,
            textAlign: "right",
          }}
        >
          {t("basket")} {diff.basket_number}
        </Typography>
      )}
    </Stack>
  );
};
